import { Table, type TableColumn } from '@dev-spendesk/grapes';
import classNames from 'classnames';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useCompanyCurrency } from 'src/core/modules/app/hooks/useCompanyCurrency';
import { type CardProductRegulatoryLimitsPerTransactionOrMonthly } from 'src/core/modules/cards/api/getCardProductRegulatoryLimits';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { type CardLimitDataRow } from '../../types/cardLimits';

import './CardLimitsTable.css';

const CardLimitDataRowLimitType = ({
  firstLabel,
  secondLabel,
}: {
  firstLabel: string;
  secondLabel?: string;
}) => {
  return (
    <div className="flex flex-col">
      <p className="text-content-primary body-m">{firstLabel}</p>
      {secondLabel && (
        <p className="text-content-secondary-bg-primary body-m">
          {secondLabel}
        </p>
      )}
    </div>
  );
};

interface CardLimitsTableProps {
  physicalCardLimits: CardProductRegulatoryLimitsPerTransactionOrMonthly;
  virtualCardLimits: CardProductRegulatoryLimitsPerTransactionOrMonthly;
  short?: boolean;
  className?: string;
}

export const CardLimitsTable = ({
  physicalCardLimits,
  virtualCardLimits,
  short,
  className,
}: CardLimitsTableProps) => {
  const { t } = useTranslation('global');
  const companyCurrency = useCompanyCurrency();

  const tableColumns: TableColumn<CardLimitDataRow>[] = [
    {
      id: 'limitType',
      header: t('generalSettings.cardLimits.table.header.limitType'),
      renderCell: (row) => row.limitType,
    },
    {
      id: 'physicalCards',
      header: t('generalSettings.cardLimits.table.header.physicalCards'),
      renderCell: (row) => row.physicalCards,
    },
    {
      id: 'virtualCards',
      header: t('generalSettings.cardLimits.table.header.virtualCards'),
      renderCell: (row) => row.virtualCards,
    },
  ];

  const tableData: CardLimitDataRow[] = [
    {
      limitType: (
        <CardLimitDataRowLimitType
          firstLabel={t(
            'generalSettings.cardLimits.table.singleTransaction.title',
          )}
          secondLabel={
            short
              ? undefined
              : t(
                  'generalSettings.cardLimits.table.singleTransaction.description',
                )
          }
        />
      ),
      physicalCards: formatMonetaryValue(
        {
          amount: physicalCardLimits.PER_TRANSACTION,
          currency: companyCurrency,
          precision: 0,
        },
        { precision: 0 },
      ),
      virtualCards: formatMonetaryValue(
        {
          amount: virtualCardLimits.PER_TRANSACTION,
          currency: companyCurrency,
          precision: 0,
        },
        { precision: 0 },
      ),
    },
    {
      limitType: (
        <CardLimitDataRowLimitType
          firstLabel={t(
            short
              ? 'generalSettings.cardLimits.table.monthlyAccumulated.titleShort'
              : 'generalSettings.cardLimits.table.monthlyAccumulated.title',
          )}
          secondLabel={
            short
              ? undefined
              : t(
                  'generalSettings.cardLimits.table.monthlyAccumulated.description',
                )
          }
        />
      ),
      physicalCards: formatMonetaryValue(
        {
          amount: physicalCardLimits.MONTHLY,
          currency: companyCurrency,
          precision: 0,
        },
        { precision: 0 },
      ),
      virtualCards: formatMonetaryValue(
        {
          amount: virtualCardLimits.MONTHLY,
          currency: companyCurrency,
          precision: 0,
        },
        { precision: 0 },
      ),
    },
  ];

  return (
    <Table
      className={classNames(
        short ? 'CardLimitSettings__table__short' : 'CardLimitSettings__table',
        className,
      )}
      columns={tableColumns}
      data={tableData}
    />
  );
};
